import React, { useCallback, useEffect, useMemo, useState } from "react";
import { formatTime } from "../../../../helpers/generalHelpers";
import { activeTemporalSegments, temporalBounds, temporalSampleStep } from "../../../../helpers/modelBuilder/temporalPresentation";
import ColourBar from "../../ColourBar";
import "./AgentTemporalTimeline.css";

const clockTime = (seconds) => formatTime(seconds, { style: "digital" });
const SPEEDS = [1, 2, 4, 8];

const TemporalRow = React.memo(function TemporalRow({ segment, min, span, active, color, onSelect }) {
  const left = ((segment.startS - min) / span) * 100;
  const width = Math.max(0.7, ((segment.endS - segment.startS) / span) * 100);
  return <button type="button" className={`agent-temporalRow ${active ? "isActive" : ""}`} onClick={() => onSelect(segment)}>
    <span className="agent-temporalLabel"><strong>{segment.label}</strong><small>{clockTime(segment.startS)}–{clockTime(segment.endS)}</small></span>
    <span className="agent-temporalTrack">
      <i className={`agent-temporalBar is-${segment.entityType}`} style={{ left: `${left}%`, width: `${Math.min(width, 100 - left)}%`, background: color || undefined }} />
    </span>
  </button>;
});

export default function AgentTemporalTimeline({ segments, time, onTimeChange, onPlay, canAdvance = true, onSelectSegment, missingGeometry = 0, magnitudeRanges = [], magnitudeColor, magnitudeLabel, onMagnitudeBoundaryChange, onMagnitudeColorChange, onMagnitudeReset }) {
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const { min, max } = useMemo(() => temporalBounds(segments), [segments]);
  const sampleStep = useMemo(() => temporalSampleStep(segments), [segments]);

  useEffect(() => {
    onTimeChange(min);
    setPlaying(false);
  }, [min, onTimeChange]);

  useEffect(() => {
    if (!playing || !canAdvance) return undefined;
    // Keep playback near 24 seconds while landing on multiples of the data's common interval.
    const step = sampleStep * Math.max(1, Math.ceil((max - min) / (120 * sampleStep)));
    let frame;
    let lastTick = 0;
    const tick = (now) => {
      if (!lastTick) lastTick = now;
      if (now - lastTick >= 200) {
        lastTick = now;
        onTimeChange((current) => Math.min(max, current + step * speed));
      }
      frame = window.requestAnimationFrame(tick);
    };
    frame = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(frame);
  }, [playing, canAdvance, min, max, sampleStep, speed, onTimeChange]);

  useEffect(() => {
    if (playing && time >= max) setPlaying(false);
  }, [playing, time, max]);

  const span = max - min;
  const activeIds = new Set(activeTemporalSegments(segments, time, max).map((segment) => segment.id));
  const selectSegment = useCallback((segment) => {
    setPlaying(false);
    onTimeChange(segment.startS);
    onSelectSegment?.(segment);
  }, [onTimeChange, onSelectSegment]);
  const play = () => {
    if (time >= max) onTimeChange(min);
    if (!playing) onPlay?.();
    setPlaying((value) => !value);
  };

  if (!segments.length) return <div className="agent-temporalEmpty">No valid timed links or nodes were provided.</div>;

  return <div className="agent-temporalTimeline">
    <div className="agent-temporalControls">
      <button type="button" onClick={play}>{playing ? "Pause" : "Play"}</button>
      <input type="range" aria-label="Timeline time" min={min} max={max} step={sampleStep} value={Math.max(min, Math.min(max, time))} onChange={(event) => onTimeChange(Number(event.target.value))} />
      <strong>{clockTime(time)}</strong>
      <div className="agent-temporalSpeed" aria-label="Playback speed">
        <button type="button" aria-label="Decrease playback speed" disabled={speed === SPEEDS[0]} onClick={() => setSpeed(SPEEDS[SPEEDS.indexOf(speed) - 1])}>−</button>
        <span>{speed}×</span>
        <button type="button" aria-label="Increase playback speed" disabled={speed === SPEEDS[SPEEDS.length - 1]} onClick={() => setSpeed(SPEEDS[SPEEDS.indexOf(speed) + 1])}>+</button>
      </div>
    </div>
    <div className="agent-temporalAxis"><span>{clockTime(min)}</span><span>Sample interval: {sampleStep} s</span><span>{clockTime(max)}</span></div>
    {!!magnitudeRanges.length && <div className="agent-temporalScale"><ColourBar scale={{ ranges: magnitudeRanges }} label={magnitudeLabel} onBoundaryChange={onMagnitudeBoundaryChange} onColorChange={onMagnitudeColorChange} onReset={onMagnitudeReset} boundaryStep={0} /></div>}
    <div className="agent-temporalRows">
      {segments.map((segment) => <TemporalRow key={segment.id} segment={segment} min={min} span={span} active={activeIds.has(segment.id)} color={magnitudeColor?.(segment.magnitude)} onSelect={selectSegment} />)}
    </div>
    {missingGeometry > 0 && <p className="agent-temporalNotice">Map geometry unavailable for {missingGeometry} step{missingGeometry === 1 ? "" : "s"}; the timeline can still be played.</p>}
  </div>;
}
